import { Button, Divider, IconButton, Stack, Typography } from '@mui/material'
import fastq from 'fastq'
import type { $KickPlate, EnhancedDomain, KickPlateBulkOrderItem } from 'metalworks-domain'
import { $KickPlateFixingHoles, ApplicationTypes, Model, createRecipe } from 'metalworks-domain'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { waitForRenderFrames } from '../../../canvas/rendering-sync'
import { exportStepFile } from '../../../export/oc'
import { configuratorInterface } from '../../../systems/configurator-interface'
import { ProgressButton } from '../FinalizationSubmitButton'
import type { FinalizationFormData } from '../shared/JobDetailsForm'
import { createGenerateImages } from '../useFinalize'
import { BulkPlateTable } from './BulkPlateTable'
import { useBulkFinalize } from './useBulkFinalize'

const getPlateUpdate = (
  rootEntityId: Model.Plate['id'],
  item: { horizontalLength: number; verticalLength: number; quantity: number }
) => {
  return configuratorInterface.updates.DomainEntityModification({
    entityId: rootEntityId,
    stateDelta: {
      horizontalLength: item.horizontalLength,
      verticalLength: item.verticalLength,
      quantity: item.quantity,
    },
    updateTag: 'applyBulkOrderItem',
  })
}

const getFixingHolesUpdate = (
  fixingHolesId: Model.KickPlateFixingHoles['id'],
  item: { horizontalHoleCount: number; verticalHoleCount: number }
) => {
  return configuratorInterface.updates.DomainEntityModification({
    entityId: fixingHolesId,
    stateDelta: {
      horizontalCount: item.horizontalHoleCount,
      verticalCount: item.verticalHoleCount,
    },
    updateTag: 'applyBulkOrderItem',
  })
}

const getBulkPlateFinalizationUpdate = (
  id: Model.KickPlateBulkOrderItem['id'],
  status: Model.BulkPlateFinalizationStatus
) => {
  return configuratorInterface.updates.DomainEntityModification({
    entityId: id,
    stateDelta: { finalizationStatus: status },
    updateTag: 'applyBulkOrderItem',
  })
}

interface BulkPlateSubmitItem {
  quoteId: number
  item: KickPlateBulkOrderItem
}

interface BulkPlateTableDialogProps {
  jobDetailsData: FinalizationFormData
  onSuccess: (returnUrl: string | null) => void
  onCancel: () => void
}

export const BulkPlateTableDialog = (props: BulkPlateTableDialogProps) => {
  const { jobDetailsData, onSuccess, onCancel } = props

  const { rootEntityId, configurationId, storeSystem, navigationSystem, domain } = configuratorInterface.useDomain()
  const { cameraControlSystem, imageRenderingSystem } = configuratorInterface.useConfigurator()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const logger = useLogger()

  const assetGenQueueRef = useRef<fastq.queue<KickPlateBulkOrderItem>>()
  const submitQueueRef = useRef<fastq.queue<BulkPlateSubmitItem>>()
  const waitQueueRef = useRef<fastq.queue<KickPlateBulkOrderItem>>()
  const [queueTick, setQueueTick] = useState(0)

  const [isGenerating, setIsGenerating] = useState(false)
  const [submittedItemCount, setSubmittedItemCount] = useState(-1)
  const isSubmitting = submittedItemCount >= 0
  const [submittingProgress, setSubmittingProgress] = useState(0)

  const imagesRef = useRef<Record<string, string | null>>({})
  const stepFilesRef = useRef<Record<string, Awaited<ReturnType<typeof exportStepFile>>>>({})

  const $plate = configuratorInterface.useRootEntityState() as $KickPlate
  const $bulkOrderItems = $plate.$bulkOrderItems

  const $fixingHoles = useMemo(() => $plate.$holes.find($KickPlateFixingHoles.is), [$plate])
  const hasUnfinishedItems = $bulkOrderItems.some(
    item =>
      item.finalizationStatus === Model.BulkPlateFinalizationStatus.New ||
      item.finalizationStatus === Model.BulkPlateFinalizationStatus.Generated ||
      item.finalizationStatus === Model.BulkPlateFinalizationStatus.Submitting
  )

  const { resolveQuoteId, finalizeItem, waitForItem, returnUrl } = useBulkFinalize()
  // The queue survives session refreshes, so use the latest callback for each item.
  const finalizeItemRef = useRef(finalizeItem)
  finalizeItemRef.current = finalizeItem

  const defaultSettings = useRef({
    horizontalLength: $plate.horizontalLength,
    verticalLength: $plate.verticalLength,
    fixingHolesId: $fixingHoles?.id ?? null,
    horizontalHoleCount: $fixingHoles?.horizontalCount ?? -1,
    verticalHoleCount: $fixingHoles?.verticalCount ?? -1,
    quantity: $plate.quantity,
  })

  const { create: createItem } = configuratorInterface.useCookedRecipes(
    [
      createRecipe({
        type: 'KickPlateBulkOrderItem',
        finalizationStatus: Model.BulkPlateFinalizationStatus.New,
        finalizationId: uuidv4(),
        horizontalLength: defaultSettings.current.horizontalLength,
        verticalLength: defaultSettings.current.verticalLength,
        horizontalHoleCount: defaultSettings.current.horizontalHoleCount,
        verticalHoleCount: defaultSettings.current.verticalHoleCount,
        quantity: 1,
      }),
    ],
    rootEntityId
  )

  const handleDelete = (item: EnhancedDomain.$KickPlateBulkOrderItem) => {
    storeSystem.stageAndCommit([
      configuratorInterface.updates.EntityDeletion({ entityId: item.id, updateTag: 'applyBulkOrderItem' }),
    ])
  }

  const generateImages = useMemo(
    () =>
      createGenerateImages(
        configurationId,
        storeSystem,
        imageRenderingSystem,
        cameraControlSystem,
        navigationSystem,
        apiClient
      ),
    [configurationId, storeSystem, imageRenderingSystem, cameraControlSystem, navigationSystem, apiClient]
  )

  // Handler for final submitting - sending whole dialog state to the backend.
  const handleSubmit = useCallback(async () => {
    const hasNewItems = $bulkOrderItems.some(item => item.finalizationStatus === Model.BulkPlateFinalizationStatus.New)
    if (hasNewItems) {
      // TODO: Nicer error message.
      alert('Please wait for all items to be generated before submitting.')
      return
    }

    const quoteId = await resolveQuoteId(jobDetailsData)

    for (const item of $bulkOrderItems) {
      if (item.finalizationStatus === Model.BulkPlateFinalizationStatus.Generated) {
        submitQueueRef.current?.push({ item, quoteId })
      } else if (item.finalizationStatus === Model.BulkPlateFinalizationStatus.Submitting) {
        waitQueueRef.current?.push(item)
      }
    }

    setSubmittedItemCount(0)
  }, [$bulkOrderItems, jobDetailsData, resolveQuoteId])

  // Queue setup. It's set up in a useMemo to avoid re-creating the queue on every render.
  useMemo(
    () => {
      if (!assetGenQueueRef.current) {
        assetGenQueueRef.current = fastq.promise(async (item: KickPlateBulkOrderItem) => {
          try {
            setIsGenerating(true)
            imagesRef.current[item.finalizationId] = null

            const itemInState = storeSystem.stateSlices.committed.value.state.entities.find(
              entity => entity.id === item.id
            )

            // If item is not in state, it was probably deleted.
            // Skip processing this item.
            if (!itemInState) {
              return
            }

            // Update main plate to generate images and STEP file.
            storeSystem.stageAndCommit([
              getPlateUpdate(rootEntityId, item),
              ...(defaultSettings.current.fixingHolesId
                ? [getFixingHolesUpdate(defaultSettings.current.fixingHolesId, item)]
                : []),
            ])

            // Wait for React Three Fiber to commit the updated plate before capture.
            await waitForRenderFrames()

            const [{ designImage, imageUploadPromise }, stepFile] = await Promise.all([
              generateImages(item.finalizationId, ApplicationTypes.NavigationState.BulkFinalization),
              exportStepFile(storeSystem.stateSlices.committed.value.state, domain),
            ])

            imagesRef.current[item.finalizationId] = URL.createObjectURL(designImage.blob)
            stepFilesRef.current[item.finalizationId] = stepFile

            const newStatus =
              item.finalizationStatus === Model.BulkPlateFinalizationStatus.New
                ? Model.BulkPlateFinalizationStatus.Generated
                : item.finalizationStatus

            // Revert changes of the main plate to original values.
            // Update bulk order item status.
            storeSystem.stageAndCommit([
              getPlateUpdate(rootEntityId, defaultSettings.current),
              getBulkPlateFinalizationUpdate(item.id, newStatus),
              ...(defaultSettings.current.fixingHolesId
                ? [getFixingHolesUpdate(defaultSettings.current.fixingHolesId, defaultSettings.current)]
                : []),
            ])

            // Make sure images were uploaded.
            await imageUploadPromise
          } catch (error) {
            console.error('BulkUpload error:', error)
          } finally {
            setIsGenerating(false)
          }
        }, 1)
      }
    },
    // Queue is created only once, so we don't need to re-create it on every render.
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    []
  )

  useMemo(
    () => {
      if (!submitQueueRef.current) {
        submitQueueRef.current = fastq.promise(async ({ item, quoteId }: BulkPlateSubmitItem) => {
          try {
            storeSystem.stageAndCommit([
              getBulkPlateFinalizationUpdate(item.id, Model.BulkPlateFinalizationStatus.Submitting),
              getPlateUpdate(rootEntityId, item),
              ...(defaultSettings.current.fixingHolesId
                ? [getFixingHolesUpdate(defaultSettings.current.fixingHolesId, item)]
                : []),
            ])

            const stepFileData = stepFilesRef.current[item.finalizationId]
            if (!stepFileData) {
              throw new Error('Step file not found during bulk plate finalization.')
            }

            const stepFileText = await stepFileData.stepFile.text()
            const plateVolume = stepFileData.volume

            await configurationPersistSystem.save()
            await finalizeItemRef.current(quoteId, jobDetailsData, stepFileText, plateVolume, item)

            waitQueueRef.current?.push(item)
          } catch (err) {
            logger.error('Error when finalizing item:', { error: err })

            storeSystem.stageAndCommit([
              getBulkPlateFinalizationUpdate(item.id, Model.BulkPlateFinalizationStatus.Failed),
            ])
          } finally {
            setSubmittedItemCount(prev => prev + 1)
            setTimeout(() => {
              setQueueTick(prev => prev + 1)
            }, 0)
          }
        }, 1)
      }
    },
    // Queue is created only once, so we don't need to re-create it on every render.
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    []
  )

  useMemo(
    () => {
      if (!waitQueueRef.current) {
        waitQueueRef.current = fastq.promise(async (item: KickPlateBulkOrderItem) => {
          try {
            await waitForItem(item)

            storeSystem.stageAndCommit([
              getBulkPlateFinalizationUpdate(item.id, Model.BulkPlateFinalizationStatus.Submitted),
            ])
          } catch (error) {
            storeSystem.stageAndCommit([
              getBulkPlateFinalizationUpdate(item.id, Model.BulkPlateFinalizationStatus.Failed),
            ])

            console.error('BulkUpload error:', error)
          } finally {
            setTimeout(() => {
              setQueueTick(prev => prev + 1)
            }, 0)
          }
        }, 10)
      }
    },
    // Queue is created only once, so we don't need to re-create it on every render.
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    []
  )

  // Add first item to the plate if there are no items yet.
  useMemo(() => {
    if ($bulkOrderItems.length === 0) {
      createItem()
    }
  }, [$bulkOrderItems, createItem])

  // Update default settings when fixing holes change.
  useEffect(
    () => {
      defaultSettings.current = Object.assign(defaultSettings.current, {
        fixingHolesId: $fixingHoles?.id ?? null,
        horizontalHoleCount: $fixingHoles?.horizontalCount ?? -1,
        verticalHoleCount: $fixingHoles?.verticalCount ?? -1,
      })
    },
    // Default settings are saved on initial render or when user changes the fixing holes.
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    [$fixingHoles?.id]
  )

  useEffect(() => {
    // It could be 0 when it started submitting and no plate has been submitted yet.
    // Check queueTick to make ESLint happy.
    if (submittedItemCount > 0 && queueTick > 0) {
      const completedItems = $bulkOrderItems.filter(
        item =>
          item.finalizationStatus === Model.BulkPlateFinalizationStatus.Submitted ||
          item.finalizationStatus === Model.BulkPlateFinalizationStatus.Failed
      )

      const progress =
        isSubmitting && $bulkOrderItems.length > 0
          ? Math.round((completedItems.length / $bulkOrderItems.length) * 100)
          : 0

      setSubmittingProgress(progress)

      if (submitQueueRef.current?.idle() && waitQueueRef.current?.idle()) {
        const allSubmitted = completedItems.length === $bulkOrderItems.length

        if (!allSubmitted) {
          // TODO: Nicer error message.
          alert('Not all plates have been submitted.')
          return
        }

        setSubmittedItemCount(-1)

        const someFailed = completedItems.some(
          item => item.finalizationStatus === Model.BulkPlateFinalizationStatus.Failed
        )

        if (!someFailed) {
          onSuccess(returnUrl)
        }
      }
    }
  }, [submittedItemCount, onSuccess, returnUrl, $bulkOrderItems, queueTick, isSubmitting])

  if (!assetGenQueueRef.current) {
    return null
  }

  return (
    <Dialog.Root
      variant="static"
      sx={{
        width: '90%',
        height: '90%',
        minWidth: '1200px',
        maxWidth: '1600px',
        minHeight: '640px',
        justifyContent: 'flex-start',
        '& .MuiCardContent-root': {
          flexGrow: 1,
          overflow: 'auto',
        },
      }}
    >
      <Dialog.ContentLayout
        controlsPosition="end"
        header={
          <>
            <Stack direction="row" sx={{ m: 4, justifyContent: 'space-between' }}>
              <Typography variant="h1">Bulk Order Kick Plates</Typography>
              <IconButton color="secondary" onClick={onCancel}>
                <PhxIcon name="X" />
              </IconButton>
            </Stack>
            <Divider />
          </>
        }
        body={
          <BulkPlateTable
            queue={assetGenQueueRef.current}
            imageUrls={imagesRef.current}
            items={$bulkOrderItems}
            hasHoles={!!$fixingHoles}
            onCreate={createItem}
            onDelete={handleDelete}
          />
        }
        controls={
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
            <Typography variant="body2" color="secondary">
              Step 2 of 2
            </Typography>
            <Stack direction="row" gap={1}>
              <Button variant="text" onClick={onCancel} startIcon={<PhxIcon name="ArrowLeft" />}>
                Cancel
              </Button>

              <ProgressButton
                text={isSubmitting ? 'Creating Quote' : 'Create Quote'}
                progress={isSubmitting ? submittingProgress : 0}
                endIcon={<PhxIcon name="ArrowRight" />}
                type="submit"
                disabled={isGenerating || isSubmitting || !hasUnfinishedItems}
                onClick={handleSubmit}
              />
            </Stack>
          </Stack>
        }
      />
    </Dialog.Root>
  )
}
